import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Card, Col, Row, Form, Input } from 'antd';
import JornadasList from 'componentsv2/Jornadas';
import {
  getBasketballMatchStatistics,
  getGroupMatchDays,
  getMatchIncidentsExpulsions,
  getMatchIncidentsFaults,
} from 'api/events';
import { useParams } from 'react-router-dom';
import BlockPageLoader from 'components/Loaders/BlockPageLoader';
import RegisterCard from 'componentsv2/RegisterCard';

const JornadasBaloncesto = () => {
  let { eventId, matchDayId, groupId, matchId } = useParams();

  const [rounds, setRounds] = useState([]);
  const [freeThrowIncidents, setFreeThrowIncidents] = useState([]);
  const [twoThrowIncidents, setTwoThrowIncidents] = useState([]);
  const [threeThrowIncidents, setThreeThrowIncidents] = useState([]);
  const [faultIncidents, setFaultIncidents] = useState([]);
  const [expulsionIncidents, setExpulsionIncidents] = useState([]);
  const [loadingGroupRounds, setLoadingGroupRounds] = useState(true);
  const [loadingFreeThrowIncidents, setLoadingFreeThrowIncidents] = useState(true);
  const [loadingTwoThrowIncidents, setLoadingTwoThrowIncidents] = useState(true);
  const [loadingThreeThrowIncidents, setLoadingThreeThrowIncidents] = useState(true);
  const [loadingFaultIncidents, setLoadingFaultIncidents] = useState(true);
  const [loadingExpulsionIncidents, setLoadingExpulsionIncidents] = useState(true);

  const roundMatches = useMemo(
    () =>
      rounds.reduce((roundsAccumulate, round) => {
        return [
          ...roundsAccumulate,
          ...round.matches.map((roundMatch) => ({
            ...roundMatch,
            round_id: round.id,
          })),
        ];
      }, []),
    [rounds]
  );
  const roundMatch = useMemo(
    () =>
      roundMatches.filter((rounMatch) => rounMatch.id === Number(matchId))[0],
    [matchId, roundMatches]
  );
  const teams = useMemo(() => (roundMatch ? [roundMatch?.home, roundMatch?.visit] : []),
  [roundMatch]);

  const teamsDropdownOptions = useMemo(
    () =>
      teams.map(team => ({
        value: String(team.id),
        label: team.name,
      })),
    [teams]
  );



  const onLoadRounds = useCallback(() => {
    setLoadingGroupRounds(true);

    getGroupMatchDays(groupId)
      .then(({ body, status }) => {
        if ([200, 201].includes(status)) {
          setRounds(body?.data?.rounds || []);
        }
        setLoadingGroupRounds(false);
      })
      .catch(() => {
        setLoadingGroupRounds(false);
      });
  }, [groupId]);

  // callbacks
  const loadFreeThrowIncidents = useCallback(() => {
    setLoadingFreeThrowIncidents(true);

    getBasketballMatchStatistics(matchId, 'free_throw')
      .then(({ status, body }) => {
        if ([200].includes(status)) {
          setFreeThrowIncidents(body?.data?.free_throws || []);
        }
        setLoadingFreeThrowIncidents(false);
      })
      .catch(() => {
        setLoadingFreeThrowIncidents(false);
      });
  }, [matchId]);

  const loadTwoThrowIncidents = useCallback(() => {
    setLoadingTwoThrowIncidents(true);

    getBasketballMatchStatistics(matchId, 'two_points')
      .then(({ status, body }) => {
        if ([200].includes(status)) {
          setTwoThrowIncidents(body?.data?.two_points || []);
        }
        setLoadingTwoThrowIncidents(false);
      })
      .catch(() => {
        setLoadingTwoThrowIncidents(false);
      });
  }, [matchId]);

  const loadThreeThrowIncidents = useCallback(() => {
    setLoadingThreeThrowIncidents(true);

    getBasketballMatchStatistics(matchId, 'three_points')
      .then(({ status, body }) => {
        if ([200].includes(status)) {
          setThreeThrowIncidents(body?.data?.three_points || []);
        }

        setLoadingThreeThrowIncidents(false);
      })
      .catch(() => {
        setLoadingThreeThrowIncidents(false);
      });
  }, [matchId]);

  const loadFaultIncidents = useCallback(() => {
    setLoadingFaultIncidents(true);

    getMatchIncidentsFaults(matchId)
      .then(({ status, body }) => {
        if ([200].includes(status)) {
          setFaultIncidents(body?.data?.incidents || []);
        }

        setLoadingFaultIncidents(false);
      })
      .catch(() => {
        setLoadingFaultIncidents(false);
      });
  }, [matchId]);

  const loadExpulsionIncidents = useCallback(() => {
    setLoadingExpulsionIncidents(true);

    getMatchIncidentsExpulsions(matchId)
      .then(({ status, body }) => {
        if ([200].includes(status)) {
          setExpulsionIncidents(body?.data?.incidents || []);
        }

        setLoadingExpulsionIncidents(false);
      })
      .catch(() => {
        setLoadingExpulsionIncidents(false);
      });
  }, [matchId]);

  const loadIncidents = useCallback(() => {
    loadFreeThrowIncidents();
    loadTwoThrowIncidents();
    loadThreeThrowIncidents();
    loadFaultIncidents();
    loadExpulsionIncidents();
  }, [
    loadFreeThrowIncidents,
    loadTwoThrowIncidents,
    loadThreeThrowIncidents,
    loadExpulsionIncidents,
    loadFaultIncidents,
  ]);

  useEffect(() => {
    onLoadRounds();
    loadIncidents();
  }, [groupId, onLoadRounds, loadIncidents]);

  const uniqueRound = rounds?.find(round => {
    return round?.id === Number(matchDayId);
  });

  const match = uniqueRound?.matches?.find(match => match?.id === Number(matchId));

  if (loadingGroupRounds) {
    return <BlockPageLoader className="min-h-screen" valignTop />;
  }

  return (
    <>
      <Card title={'Editar Encuentro'}>
        <JornadasList key={match?.id} match={match} isEdit={true} displayOnly={true} />
        <div
          style={{
            marginTop: '34px',
            marginBottom: '34px',
            fontSize: '26px',
            display: 'flex',
            justifyContent: 'center',
          }}
          className="avatar-status-name"
        >
          Estadisticas Individuales
        </div>
        <Row gutter={[16, 0]} justify={'space-between'}>
          <Col flex xs={24} sm={24} md={12} lg={12} xl={12}>
            <RegisterCard
              data={freeThrowIncidents}
              teamList={teamsDropdownOptions}
              incidentType="free_throw"
              title={'Tiros libres'}
              reloadStats={loadFreeThrowIncidents}
            >
              <Form.Item
                label="Tiros anotados"
                name="free_throw"
                rules={[
                  {
                    required: true,
                    message: 'Ingrese el número de tiros libres anotados!',
                  },
                ]}
              >
                <Input />
              </Form.Item>
            </RegisterCard>
          </Col>
          <Col flex xs={24} sm={24} md={12} lg={12} xl={12}>
            <RegisterCard
              data={twoThrowIncidents}
              teamList={teamsDropdownOptions}
              incidentType="two_points"
              title={'Dobles'}
              reloadStats={loadTwoThrowIncidents}
            >
              <Form.Item
                label="Dobles anotados"
                name="two_points"
                rules={[
                  {
                    required: true,
                    message: 'Ingrese el número de dobles anotados!',
                  },
                ]}
              >
                <Input />
              </Form.Item>
            </RegisterCard>
          </Col>
          <Col flex xs={24} sm={24} md={12} lg={12} xl={12}>
            <RegisterCard
              data={threeThrowIncidents}
              teamList={teamsDropdownOptions}
              incidentType="three_points"
              title={'Triples'}
              reloadStats={loadThreeThrowIncidents}
            >
              <Form.Item
                label="Triples anotados"
                name="three_points"
                rules={[
                  {
                    required: true,
                    message: 'Ingrese el número de triples anotados!',
                  },
                ]}
              >
                <Input />
              </Form.Item>
            </RegisterCard>
          </Col>
          <Col flex xs={24} sm={24} md={12} lg={12} xl={12}>
            <RegisterCard
              data={faultIncidents}
              teamList={teamsDropdownOptions}
              incidentType="fault"
              title={'Registro de Faltas'}
              reloadStats={loadFaultIncidents}
            />
          </Col>
          <Col flex xs={24} sm={24} md={12} lg={12} xl={12}>
            <RegisterCard
              data={expulsionIncidents}
              teamList={teamsDropdownOptions}
              incidentType="expulsion"
              title={'Expulsados'}
              reloadStats={loadExpulsionIncidents}
            />
          </Col>
        </Row>
      </Card>
    </>
  );
};

export default JornadasBaloncesto;
